import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getQueryKey } from '@/api/utils/getQueryKey';
import { handleApiCall } from '@/api/utils/handleApiCall';
import { useAuth } from '@/context/AuthProvider';

export interface CreateReferralDto {
  referralType: string;
  referralId?: string;
  referralUrl: string;
  referralMetadata?: Record<string, unknown>;
}

export interface ReferralResponse {
  id: string;
  user_id: string;
  referral_type: string;
  referral_id?: string;
  referral_url: string;
  referral_metadata?: Record<string, unknown>;
  is_redirected: boolean;
  created_at: string;
  updated_at: string;
  redirected_at?: string;
}

// API Response Types
export type ApiResponse_GetUserReferral = ReferralResponse | null;
export type ApiResponse_CreateReferral = ReferralResponse;
export type ApiResponse_CompleteReferral = ReferralResponse;

// Query Keys
const GET_METHOD = 'GET';
const POST_METHOD = 'POST';
const DELETE_METHOD = 'DELETE';
const REFERRAL_ENDPOINT = 'user/referral';
const COMPLETE_REFERRAL_ENDPOINT = 'user/referral/complete';

export const QUERY_KEY_GET_REFERRAL = () => getQueryKey(GET_METHOD, REFERRAL_ENDPOINT);

export const useCreateReferral = () => {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<ApiResponse_CreateReferral, Error, CreateReferralDto>({
    mutationFn: async (data) =>
      handleApiCall<ApiResponse_CreateReferral, CreateReferralDto>(
        session,
        POST_METHOD,
        REFERRAL_ENDPOINT,
        data
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY_GET_REFERRAL() });
    },
  });
};

export const useGetUserReferral = (enabled = true) => {
  const { session } = useAuth();

  return useQuery<ApiResponse_GetUserReferral, Error>({
    enabled: enabled && !!session?.access_token,
    queryKey: QUERY_KEY_GET_REFERRAL(),
    queryFn: async () => {
      const data = await handleApiCall<ApiResponse_GetUserReferral>(
        session,
        GET_METHOD,
        REFERRAL_ENDPOINT
      );
      return data;
    },
    retry: false,
  });
};

export const useCompleteReferral = () => {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<ApiResponse_CompleteReferral, Error>({
    mutationFn: async () =>
      handleApiCall<ApiResponse_CompleteReferral>(
        session,
        POST_METHOD,
        COMPLETE_REFERRAL_ENDPOINT
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY_GET_REFERRAL() });
    },
  });
};

export const useDeleteReferral = () => {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<void, Error>({
    mutationFn: async () => {
      await handleApiCall<void>(
        session,
        DELETE_METHOD,
        REFERRAL_ENDPOINT
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY_GET_REFERRAL() });
    },
  });
};